/**
 * WorkflowRunnerPage · The operator's guided walkthrough shell.
 * ─────────────────────────────────────────────────────────────
 * URL: /workflow/{run_id}
 *
 * Renders: status header · task label · progress · current action (dispatched
 * to the right renderer) · captured-data debug pane.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Home, CheckCircle2, X, ArrowRight } from 'lucide-react';
import dispatchRenderer from './rendererDispatcher';
import DuplicateAppraisalPrompt from './components/DuplicateAppraisalPrompt';
import { STATUS_LABELS, useStatusLabels } from './pipelineApi';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use(cfg => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

/**
 * Pre-UAT v10 · B3 fix — surface REAL mirror-write failures.
 * Historically these calls were `catch (e) { silent }` so any 5xx
 * or network error left the appraisal DB out of sync with the runner's
 * captured_data — silent divergence, downstream PDFs missing data.
 *
 * We stay silent on 4xx (backend allowlist correctly rejected an unknown
 * field — expected) and toast only on 5xx / network failures / offline.
 */
function mirrorWriteFail(err, fieldName) {
  const status = err?.response?.status;
  const isServerErr = !err?.response || status >= 500;
  if (isServerErr) {
    // eslint-disable-next-line no-console
    console.warn('[runner] mirror write failed:', fieldName, err);
    toast.error(`Field save failed (${fieldName}) — check network + retry the step`, {
      duration: 6000,
    });
  }
  // 4xx: field wasn't allowlisted → expected, stay silent.
}

/** iter303ai-fix8 · Robust dotted-key lookup. Handles BOTH shapes:
 *    · flat: captured_data["appraisal.report_variant"] = "free_estimate"
 *    · nested: captured_data.appraisal.report_variant = "free_estimate"
 *  Radio-choice + primitive writers store as FLAT (write_to has a dot).
 *  This helper tries the flat form first, then falls back to nested walk. */
function readKey(data, key) {
  if (!data || !key) return undefined;
  // 1. Flat key (fastest — most write_to values land here).
  if (Object.prototype.hasOwnProperty.call(data, key)) return data[key];
  // 2. Nested walk.
  const parts = key.split('.');
  let v = data;
  for (const p of parts) {
    if (v == null || typeof v !== 'object') return undefined;
    v = v[p];
  }
  return v;
}

/** Evaluate a simple `field == value` or `field in [a,b,c]` trigger condition
 *  against captured_data. Missing / falsy conditions always pass. Values are
 *  compared as strings. */
function evaluateCondition(cond, data) {
  if (!cond) return true;
  // iter303ak-fix49 · Resolve parentheses recursively — inner-first —
  // then eval the flattened expression. Supports arbitrary nesting.
  let s = cond;
  const parenRe = /\(([^()]+)\)/;
  while (parenRe.test(s)) {
    s = s.replace(parenRe, (_, inner) => evaluateCondition(inner, data) ? 'true' : 'false');
  }
  if (s.trim() === 'true') return true;
  if (s.trim() === 'false') return false;
  // iter303ak-fix48 · Compound conditions — `&&` (AND) and `||` (OR).
  // Both are precedence-agnostic (no mixed nesting supported without
  // parentheses). Use parens above for grouping.
  if (s.includes('&&')) {
    return s.split('&&').every(sub => evaluateCondition(sub.trim(), data));
  }
  if (s.includes('||')) {
    return s.split('||').some(sub => evaluateCondition(sub.trim(), data));
  }
  // At this point s is a leaf expression (no parens, no && / ||).
  cond = s;
  // `field in [a, b, c]` (Iter303ae) — matches if field's value is in the
  // bracket list. Strings can be quoted or bare; whitespace ignored.
  const mIn = cond.match(/^\s*([a-z0-9_.]+)\s+in\s+\[([^\]]+)\]\s*$/i);
  if (mIn) {
    const [, key, listStr] = mIn;
    const v = readKey(data, key);
    const items = listStr.split(',').map(s => s.trim().replace(/^['"]|['"]$/g, ''));
    return items.includes(String(v));
  }
  // iter303ak-fix45 · `field != value` — negated equality, needed for
  // outcome-driven auto-jumps (e.g. skip T30 when T20 outcome == needs plan).
  const mNe = cond.match(/^\s*([a-z0-9_.]+)\s*!=\s*['"]?([^'"]+)['"]?\s*$/i);
  if (mNe) {
    const [, key, expected] = mNe;
    const v = readKey(data, key);
    return String(v) !== String(expected);
  }
  const m = cond.match(/^\s*([a-z0-9_.]+)\s*==\s*['"]?([^'"]+)['"]?\s*$/i);
  if (!m) return true;
  const [, key, expected] = m;
  const v = readKey(data, key);
  return String(v) === String(expected);
}

/** Walk `flat` from `startIdx` in `direction` (+1 forward / -1 backward) and
 *  return the first index whose action's trigger_when passes against
 *  `capturedData`. Returns -1 if none found (i.e. we're at the boundary).
 *
 *  Iter303ai-fix6 · Descendants of failing parents are skipped too. Each flat
 *  entry carries `path: [ancestor_ids..., self_id]`. If ANY ancestor's own
 *  trigger_when evaluates false against the same captured_data, the entry is
 *  skipped regardless of its own trigger_when. This is essential when an
 *  operator back-navigates and changes an upstream answer — the walker must
 *  bypass the stale sub-branch instead of re-entering it because the sub's
 *  own trigger still references the previously-captured branch value. */
// "No safety cost" picked at Height Safety → skip the costing / supplier steps.
const HS_SKIP_WHEN_NONE = new Set(['height_safety_estimate', 'height_safety_quote_request_preview', 'height_safety_quote_request_send', 'supplier_quote_capture_multi', 'supplier_pick_winner']);

function findNextActive(startIdx, direction, flat, capturedData) {
  // Precompute action-by-id map so we can look up ancestor conditions.
  const byId = {};
  for (const e of flat) byId[e.action.id] = e;

  const isSkipped = (entry) => {
    if (HS_SKIP_WHEN_NONE.has(entry?.action?.type) && (capturedData?.height_safety_types || []).includes('none')) return true;
    const path = entry?.path || [];
    // Check every ancestor (path excluding the last id which is self).
    for (const ancId of path.slice(0, -1)) {
      const anc = byId[ancId];
      if (!anc) continue;
      const cond = anc.action?.params?.trigger_when;
      if (!evaluateCondition(cond, capturedData)) return true;
    }
    // Check the entry's own trigger.
    const cond = entry?.action?.params?.trigger_when;
    if (!evaluateCondition(cond, capturedData)) return true;
    return false;
  };

  let i = startIdx;
  while (i >= 0 && i < flat.length) {
    if (!isSkipped(flat[i])) return i;
    i += direction;
  }
  return -1;
}

export default function WorkflowRunnerPage({ embeddedRunId, onRunFinished }) {
  const { runId: paramRunId } = useParams();
  const runId = embeddedRunId || paramRunId;
  const embedded = !!embeddedRunId;
  const navigate = useNavigate();
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [inflightValue, setInflightValue] = useState(null);
  const [saving, setSaving] = useState(false);
  // iter303ai-fix7 · Finalize state for task-complete → next-status bridge.
  const [finalizing, setFinalizing] = useState(false);
  const [finalizeError, setFinalizeError] = useState('');
  const [dupe, setDupe] = useState(null);
  const stageLabels = useStatusLabels();

  const loadRun = useCallback(async (opts = {}) => {
    // `silent: true` — used by template-edit PATCHes (e.g. reorder prompts,
    // inline label edits). Skips the loading spinner AND preserves any
    // in-flight autosave value so the intake form stays mounted and the
    // user's typed-but-not-yet-committed data isn't wiped. Non-silent
    // reloads (initial mount, advance, back) still toggle the spinner.
    if (!opts.silent) { setLoading(true); }
    setError('');
    try {
      const res = await api.get(`/workflow-runs/${runId}`);
      setRun(res.data);
      if (!opts.silent) setInflightValue(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { if (!opts.silent) setLoading(false); }
  }, [runId]);

  useEffect(() => { loadRun(); }, [loadRun]);

  // v10.23 (Feb 2026 · UAT A999617 quote_sent freeze) — Safety net for
  // the standalone runner. React Router does NOT unmount
  // WorkflowRunnerPage when only the `:runId` param changes (same route
  // pattern), so `finalizing` and `finalizeError` from the previous run
  // would carry over to the new run's mount → "Finalizing…" spinner
  // stays on-screen forever even though the URL correctly moved to the
  // new run id. Reset both whenever `runId` changes so every fresh run
  // starts with a clean slate.
  useEffect(() => {
    setFinalizing(false);
    setFinalizeError('');
  }, [runId]);

  // iter303ak-fix2 · Hydrate appraisal_record into captured_data so the
  // banner + downstream renderers can read appraisal.appraisal_no,
  // appraisal.report_variant, etc. without each renderer refetching.
  // Only fires when appraisal_id is known but appraisal_record isn't
  // in captured_data yet.
  useEffect(() => {
    const cd = run?.captured_data || {};
    const appraisalId = cd.appraisal_id;
    const alreadyHydrated = cd.appraisal_record?.appraisal_no || cd.appraisal?.appraisal_no;
    if (!appraisalId || alreadyHydrated) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/appraisals/${appraisalId}`);
        if (cancelled) return;
        setRun(prev => prev ? {
          ...prev,
          captured_data: { ...(prev.captured_data || {}), appraisal_record: res.data, appraisal: res.data },
        } : prev);
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [run?.captured_data?.appraisal_id, run?.captured_data?.appraisal_record?.appraisal_no]);

  const flat = run?._flat_actions || [];
  const currentIdx = useMemo(() => {
    if (!run || !run.current_action_id) return -1;
    return flat.findIndex(e => e.action.id === run.current_action_id);
  }, [flat, run]);

  const current = currentIdx >= 0 ? flat[currentIdx] : null;
  // Pre-UAT v10.13 fix (Feb 2026 · testing agent iteration 318) — a freshly-
  // started run has `current_action_id=null` UNTIL the first action is
  // picked (either by start_run's seed or the initial advance PATCH).
  // Previously `isComplete = run.current_action_id == null` treated that
  // pre-first-pick state as "task complete", flashing the green
  // Task-Complete screen and hiding the first action's renderer. Correct
  // rule: task is complete when the backend explicitly marks the run
  // as `task_complete`, OR the runner has already ticked at least one
  // action AND now has no current action (i.e. really finished).
  const _completedCount = Array.isArray(run?.completed_actions) ? run.completed_actions.length : 0;
  const isComplete = !!run && (
    run.status === 'task_complete'
    || (run.current_action_id == null && _completedCount > 0)
  );

  const advance = async (overrideValue) => {
    if (!current) return;
    setSaving(true);
    try {
      const params = current.action.params || {};
      // Steps without an explicit write_to still keep their value on the run (Back + later conditions).
      const _dk = { letter_content_toggle: params.block_key, optional_inclusion: params.content_ref, generate_pdf_preview: params.template_key,
        preview_confirm_gate: params.pdf_key, field_writeback: params.field, spawn_task: params.seed_data || params.task_type }[current.action.type];
      const writeTo = params.write_to || params.capture_selection_to || (_dk ? `${current.action.type}.${_dk}` : undefined);
      // Guard: renderers commonly pass `onClick={onNext}` which forwards a
      // React SyntheticEvent as the first argument. That's not a value — it
      // has circular refs and would crash JSON.stringify. Only accept an
      // override that's a primitive, an array, or a plain data object.
      const isReactEvent = overrideValue && typeof overrideValue === 'object'
        && ('nativeEvent' in overrideValue || '_reactName' in overrideValue || overrideValue.constructor?.name === 'SyntheticBaseEvent');
      const useOverride = overrideValue !== undefined && !isReactEvent;
      const val = useOverride ? overrideValue : inflightValue;
      let patch = {};
      // Value shape: `{ __refresh: {property_record|client_record: ...}, confirmed: true }`
      // is produced by GotoForm after a pop-out window closes — it lets us
      // update the workflow context banner with fresh data without a full
      // template change.
      if (val && typeof val === 'object' && val.__refresh) {
        Object.assign(patch, val.__refresh);
      }
      // For open_intake_form, the inflight value is an object with client_id + form.
      // Persist form dict alongside the write_to (client_id) so Back can hydrate.
      // Also persists auto-created property_id + property_record so downstream
      // actions (Yes/No radio, GotoForm to property detail) can substitute
      // {property_id} in their route templates.
      if (current.action.type === 'open_intake_form' && val && typeof val === 'object') {
        if (writeTo && val.client_id) patch[writeTo] = val.client_id;
        if (val.form) patch['client_intake_form'] = val.form;
        if (val.client_record) patch['client_record'] = val.client_record;
        if (val.property_id) patch['property_id'] = val.property_id;
        if (val.property_record) patch['property_record'] = val.property_record;
        // Iter303ag-fix9 — ExistingClientIntakeForm writes `same_property`
        // ('yes' or 'no') so the downstream conditional
        // create_new_property_form action's trigger_when
        // (`same_property == no`) resolves against captured_data.
        if (val.same_property) patch['same_property'] = val.same_property;
      } else if (current.action.type === 'open_property_intake_form' && val && typeof val === 'object') {
        // Property intake — persist form + refreshed property record.
        //
        // Iter303ag-fix2 — Bug 1: there are 4 different open_property_intake_form
        // actions in Client Data (Property #5, Tenant #9, Agent-Yes #11,
        // Agent-Scripted #14). Historically all four wrote to a single
        // `property_intake_form` slot, so each new action clobbered the
        // previous one and Back → earlier action showed empty fields.
        // Fix: keep a per-action map `property_intake_forms[actionId]` so
        // each action gets its own persisted slot. The legacy single-slot
        // key is kept for back-compat with any older code paths.
        if (val.property_id) patch['property_id'] = val.property_id;
        if (val.property_record) patch['property_record'] = val.property_record;
        const formData = val.property_intake_form || val.form;
        if (formData) {
          const existingForms = (run.captured_data && run.captured_data.property_intake_forms) || {};
          patch['property_intake_forms'] = { ...existingForms, [current.action.id]: formData };
          patch['property_intake_form'] = formData;   // legacy single slot
        }
      } else if (current.action.type === 'create_new_property_form' && val && typeof val === 'object') {
        // Iter303ag-fix7 — Inline "add a different property" flow. When
        // the operator picks "No" at Action 2 they enter a brand-new
        // appraisal property via CreateNewPropertyForm. On save we SWAP
        // captured_data.property_id from the auto-created home property
        // to the new appraisal property so all downstream actions
        // (Tenant, Agent, Roof intake, Urgent Concerns, …) target the
        // right record. The old home property_id is preserved under
        // `home_property_id` for reference (also flagged with a note on
        // the property itself so the client page can distinguish them).
        if (val.new_property_id) {
          const currentPid = (run.captured_data || {}).property_id;
          if (currentPid && currentPid !== val.new_property_id) {
            patch['home_property_id'] = currentPid;
          }
          patch['property_id'] = val.new_property_id;
          patch['new_property_id'] = val.new_property_id;
        }
        if (val.property_record) patch['property_record'] = val.property_record;
        const formData = val.property_intake_form || val.form;
        if (formData) {
          const existingForms = (run.captured_data && run.captured_data.property_intake_forms) || {};
          patch['property_intake_forms'] = { ...existingForms, [current.action.id]: formData };
          patch['property_intake_form'] = formData;
        }
      } else if (writeTo && val != null && val !== '') {
        patch = { [writeTo]: val };
      }
      // Iter303ah — if the write_to targets an appraisal.* field, mirror
      // it onto the actual appraisal DB record so downstream reports and
      // pages can read it. This covers primitives like radio_choice /
      // scripted_field_prompt that only capture to run state today. The
      // PATCH endpoint is allowlisted so unknown fields are ignored
      // safely — we only send fields that begin with `appraisal.`.
      const appraisalId = (run.captured_data || {}).appraisal_id;
      if (appraisalId && writeTo && writeTo.startsWith('appraisal.') && val !== undefined && val !== null && val !== '') {
        try {
          const field = writeTo.slice('appraisal.'.length);
          await api.patch(`/appraisals/${appraisalId}`, { [field]: val });
        } catch (e) { mirrorWriteFail(e, writeTo); }  // Pre-UAT v10 · B3 fix
      }
      // iter303ak-fix45 · Mirror top-level allowlisted appraisal fields
      // (like overdue_contact_call, overdue_warning_call, payment_plan)
      // to the appraisal DB immediately so the very next letter_preview
      // action can render outcome-adaptive content. Without this, follow-
      // up capture only lives on workflow_run.captured_data until the
      // eventual send_bundle fires — leaving the operator's letter draft
      // preview with no personalised details.
      const APPRAISAL_MIRROR_KEYS = new Set([
        'overdue_contact_call', 'overdue_warning_call', 'payment_plan',
        'invoiced_payments', 'overdue_reminder_channels', 'overdue_warning_channels',
        'payment_plan_channels', 'overdue_reminder_send_result',
        'overdue_warning_send_result', 'payment_plan_send_result',
      ]);
      if (appraisalId && writeTo && APPRAISAL_MIRROR_KEYS.has(writeTo) && val !== undefined && val !== null && val !== '') {
        try {
          await api.patch(`/appraisals/${appraisalId}`, { [writeTo]: val });
        } catch (e) { mirrorWriteFail(e, writeTo); }  // Pre-UAT v10 · B3 fix
      }
      // Pre-UAT v10.6 · findNextActive edge-case fix ─────────────────
      // `findNextActive` returns -1 in TWO distinct cases:
      //   (a) LEGIT end of task — no more actions in the flat list
      //   (b) BUG hiccup — evaluateCondition failed on an unparseable
      //       trigger_when and treated all remaining actions as skipped
      //
      // Before this fix, both cases sent `current_action_id: null`,
      // which the backend (post B1) honours as "task complete". Result:
      // a single trigger_when eval hiccup would end the task early and
      // skip the remaining steps (roof screenshot, template, details,
      // etc. — the Schedule Appraisal bug reported during UAT).
      //
      // Fix: only send `null` when we're LITERALLY past the last action
      // in flat[]. Otherwise omit the field so the backend falls back
      // to safe naive idx+1 (the original pre-B1 behaviour).
      const runPatch = {
        captured_data_patch: patch,
        completed_action_id: current.action.id,
      };
      const nextCap = { ...(run.captured_data || {}), ...patch };
      if (currentIdx + 1 >= flat.length) {
        // (a) TRULY past end of task — safe to signal completion.
        runPatch.current_action_id = null;
      } else {
        const nxt = findNextActive(currentIdx + 1, +1, flat, nextCap);
        if (nxt >= 0) {
          // Normal path — advance to the next active action.
          runPatch.current_action_id = flat[nxt].action.id;
        }
        // else: omit the key. Backend does naive idx+1 (safe default).
      }
      await api.patch(`/workflow-runs/${runId}`, runPatch);
      await loadRun();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  const back = async () => {
    if (currentIdx <= 0) return;
    // Walk backward through the flat list, skipping actions whose
    // trigger_when doesn't pass — this stops the "flash then bounce forward"
    // bug where landing on an inapplicable branch immediately re-skipped.
    const prevIdx = findNextActive(currentIdx - 1, -1, flat, run.captured_data || {});
    if (prevIdx < 0) return;
    const prev = flat[prevIdx];
    setSaving(true);
    try {
      // Iter303ai-fix6 · Part B — Clear stale captured_data keys for every
      // action from prevIdx through currentIdx (inclusive). This prevents
      // the "changed my answer but the runner still routes me down the old
      // branch" bug when downstream trigger_when clauses reference values
      // captured during the earlier walk-through.
      const keysToClear = new Set();
      const idsToUncomplete = new Set();
      // Pre-UAT v10 · B2 fix — mirrored fields to revert on the appraisal.
      // Historically Back only cleared run-local captured_data, leaving the
      // appraisal DB with stale mirrored values from the earlier walk.
      // Result: letters + reports rendered old personalised content when
      // Trevor changed an upstream answer. Now we collect every mirrored
      // write_to in the un-completed range and PATCH the appraisal with
      // empty values so the record + captured_data stay in sync.
      const appraisalMirrorFieldsToRevert = new Set();
      const APPRAISAL_MIRROR_KEYS = new Set([
        'overdue_contact_call', 'overdue_warning_call', 'payment_plan',
        'invoiced_payments', 'overdue_reminder_channels', 'overdue_warning_channels',
        'payment_plan_channels', 'overdue_reminder_send_result',
        'overdue_warning_send_result', 'payment_plan_send_result',
      ]);
      for (let i = prevIdx; i <= currentIdx; i++) {
        const a = flat[i]?.action;
        if (!a) continue;
        idsToUncomplete.add(a.id);
        const wt = a.params?.write_to;
        // Only clear plain data keys — leave session-level context (property_id,
        // client_id, appraisal_id, property_record, client_record) intact so
        // the runner shell + intake forms keep their bearings.
        const PROTECTED = new Set([
          'property_id', 'client_id', 'appraisal_id',
          'property_record', 'client_record',
          'home_property_id', 'new_property_id',
        ]);
        if (wt && !PROTECTED.has(wt) && !wt.startsWith('appraisal.')) {
          keysToClear.add(wt);
        }
        // Per-action intake-form slot (namespaced map iter303ag-fix2).
        keysToClear.add(`property_intake_forms.${a.id}`);
        // Pre-UAT v10 · B2 fix — collect mirrored fields to revert.
        if (wt && wt.startsWith('appraisal.') && !PROTECTED.has(wt)) {
          appraisalMirrorFieldsToRevert.add(wt.slice('appraisal.'.length));
        }
        if (wt && APPRAISAL_MIRROR_KEYS.has(wt)) {
          appraisalMirrorFieldsToRevert.add(wt);
        }
      }
      const newCompleted = (run.completed_actions || []).filter(id => !idsToUncomplete.has(id));

      // Pre-UAT v10 · B2 fix — revert mirrored appraisal fields BEFORE the
      // run PATCH so the appraisal record + captured_data land in sync.
      const appraisalId = (run.captured_data || {}).appraisal_id;
      if (appraisalId && appraisalMirrorFieldsToRevert.size > 0) {
        const revertPayload = {};
        for (const f of appraisalMirrorFieldsToRevert) {
          revertPayload[f] = null;  // backend allowlist decides which fields accept null
        }
        try {
          await api.patch(`/appraisals/${appraisalId}`, revertPayload);
        } catch (e) {
          mirrorWriteFail(e, `Back-revert (${appraisalMirrorFieldsToRevert.size} fields)`);
        }
      }

      await api.patch(`/workflow-runs/${runId}`, {
        current_action_id: prev.action.id,
        captured_data_keys_to_clear: Array.from(keysToClear),
        completed_actions_override: newCompleted,
      });
      await loadRun();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  // iter303ak-fix7 · jumpToAction — direct navigation to any action_id
  // WITHOUT clearing captured_data or completed_actions. Used by the
  // CompletenessPreview "Jump to Action N →" buttons and by the "Return
  // to Preview" pill so the operator can hop around without losing work.
  //
  // options.divertedFrom — if set, records the action_id we JUMPED FROM
  // so the shell renders a sticky "↩ Return to Preview" pill until the
  // operator returns. Passing null clears the diversion.
  const [divertedFromActionId, setDivertedFromActionId] = useState(null);
  const jumpToAction = async (targetActionId, opts = {}) => {
    if (!targetActionId) return;
    setSaving(true);
    try {
      await api.patch(`/workflow-runs/${runId}`, {
        current_action_id: targetActionId,
      });
      if (opts.divertedFrom !== undefined) {
        setDivertedFromActionId(opts.divertedFrom);
      }
      await loadRun();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  const abandon = async () => {
    if (!window.confirm('Abandon this workflow run? You can resume later from the start page.')) return;
    await api.delete(`/workflow-runs/${runId}`);
    if (embedded && onRunFinished) return onRunFinished();
    navigate('/workflow/start');
  };

  // Iter303ai-fix4 · Smart Cancel — renderers call this instead of
  // duplicating "if canBack then onBack else navigate". Behaviour:
  //   · currentIdx > 0 → go back to the previous applicable action
  //   · currentIdx == 0 (first action of the task) → confirm-abandon
  //     the run and return to the entry chooser (embedded) or the
  //     picker (legacy /workflow/{runId} route).
  const cancel = async () => {
    if (currentIdx > 0) return back();
    if (!window.confirm('Cancel this task and return to task selection? Your progress in this task will be discarded.')) return;
    try { await api.delete(`/workflow-runs/${runId}`); } catch { /* silent */ }
    if (embedded && onRunFinished) return onRunFinished();
    navigate('/workflow/start');
  };

  // iter303ai-fix10 · When a task completes, we need to know whether more
  // sibling tasks remain in the current status so the button reads
  // "Continue to next task" (mid-status) vs "Continue to next status" (last
  // task). Fetch the status workflow doc lazily when the task hits complete.
  // iter303aj-fix7 · Also capture task_mode so exclusive statuses (Client
  // Data · New XOR Existing) skip the sibling-Next prediction and jump
  // straight to the next-status CTA text.
  const [siblingTasks, setSiblingTasks] = useState([]);
  const [siblingMode, setSiblingMode] = useState('sequential');
  useEffect(() => {
    if (!run || !run.status_key) return;
    (async () => {
      try {
        const res = await api.get(`/status-workflows/workflow/${run.status_key}`);
        setSiblingTasks(res.data?.tasks || []);
        setSiblingMode(res.data?.task_mode || 'sequential');
      } catch { /* silent */ }
    })();
  }, [run?.status_key, runId]);

  // iter303ai-fix7 · Bridge to the next pipeline status. Called from the
  // Task Complete screen. Auto-creates an appraisal from captured_data if
  // one wasn't linked (New/Existing Client flow), marks the current status
  // complete + unlocks the next, and navigates the operator straight into
  // the console focused on the newly-unlocked status.
  //
  // iter303ai-fix10 · Now sibling-aware. If the status has more tasks left,
  // starts a new run for the NEXT TASK inside the same status (no status
  // change). If it's the last task, unlocks the next status as before.
  const finalize = async (finalizeBody = {}) => {
    setFinalizing(true); setFinalizeError(''); setDupe(null);
    try {
      // S8a bug 2 fix (2026-02c) · when the last-action PATCH already
      // ran the full finalize path inline, `run.status === 'task_complete'`
      // AND `run.next_run_id` is populated. Skip the redundant
      // /finalize POST and navigate straight. Falls back to POST
      // /finalize for the client_data intake path (no appraisal_id yet).
      //
      // Pre-UAT v10.10 (Feb 2026 · UAT A999565) — MID-STATUS SIBLING HOP
      // ─────────────────────────────────────────────────────────────
      // The PATCH endpoint's siblings-aware fix now stamps `next_task_id`
      // on the run doc when more tasks remain in the current status.
      // Recognise it here and POST a fresh sibling run directly, without
      // a redundant /finalize round-trip. Without this branch, the
      // shortcut fell through to `navigate('/workflow/appraisal/{id}')`
      // (the picker page), and the console then re-selected a stale
      // `task_complete` run — trapping the operator in a Task-Complete
      // loop.
      if (run?.status === 'task_complete' && run?.appraisal_id) {
        const appraisal_id = run.appraisal_id;
        const next_run_id  = run.next_run_id;
        if (next_run_id) {
          if (embedded && onRunFinished) {
            // v10.16 · MUST await, not return, so if the parent's remount
            // races the state batch and the child stays mounted, we still
            // clear `finalizing` below and the button stops spinning.
            // eslint-disable-next-line no-console
            console.log('[runner-finalize] handoff → parent (next_run_id branch)', { next_run_id });
            await onRunFinished({ appraisal_id, next_run_id, next_status_key: null });
            // eslint-disable-next-line no-console
            console.log('[runner-finalize] parent onRunFinished resolved · clearing spinner');
            setFinalizing(false);
            return;
          }
          return navigate(`/workflow/${next_run_id}`);
        }
        // Mid-status sibling — PATCH exposed next_task_id on the run.
        if (run.next_task_id) {
          // v10.14 (Feb 2026 · UAT A999575 finalize freeze) · explicit
          // 25s timeout so a stalled POST surfaces an error banner
          // instead of leaving the spinner running forever. Previously
          // a flaky network could hang the promise indefinitely.
          // v10.15 · diagnostic console logs so if the freeze recurs
          // the user can open DevTools (F12 · Console tab) and share
          // exactly where the flow stalls.
          // eslint-disable-next-line no-console
          console.log('[runner-finalize] mid-status hop starting',
            { runId, next_task_id: run.next_task_id, status_key: run.status_key, appraisal_id });
          const newRun = await api.post(`/workflow-runs`, {
            status_key: run.status_key,
            appraisal_id,
            task_id: run.next_task_id,
          }, { timeout: 25000 });
          const newRunId = newRun.data?.id;
          // eslint-disable-next-line no-console
          console.log('[runner-finalize] mid-status hop POST OK · new run:', newRunId);
          if (embedded && onRunFinished) {
            // v10.16 · await + clear spinner (see comment above).
            // eslint-disable-next-line no-console
            console.log('[runner-finalize] handoff → parent (mid-status branch)', { newRunId });
            await onRunFinished({ appraisal_id, next_run_id: newRunId, next_status_key: null });
            // eslint-disable-next-line no-console
            console.log('[runner-finalize] parent onRunFinished resolved · clearing spinner');
            setFinalizing(false);
            return;
          }
          if (newRunId) {
            // v10.23 (Feb 2026 · UAT A999617 quote_sent freeze) — Standalone
            // (non-embedded) runners must clear the finalizing spinner BEFORE
            // navigating. React Router doesn't unmount WorkflowRunnerPage
            // when only the `:runId` param changes (route stays the same);
            // the same component instance survives → `finalizing=true` sticks
            // → "Finalizing…" spinner never clears even though the URL bar
            // moves to the new run. Also see the safety-net useEffect that
            // resets the spinner on every runId change.
            setFinalizing(false);
            return navigate(`/workflow/${newRunId}`);
          }
        }
        if (embedded && onRunFinished) {
          await onRunFinished({ appraisal_id, next_status_key: null });
          setFinalizing(false);
          return;
        }
        return navigate(`/workflow/appraisal/${appraisal_id}`);
      }

      const res = await api.post(`/workflow-runs/${runId}/finalize`, finalizeBody, { timeout: 25000 });
      const { appraisal_id, next_status_key, next_task_id, is_last_task, next_run_id } = res.data;

      // Case A · More tasks remain in the current status.
      if (!is_last_task && next_task_id && appraisal_id) {
        // Start a fresh run for the next sibling task and hand off.
        const newRun = await api.post(`/workflow-runs`, {
          status_key: run.status_key,
          appraisal_id,
          task_id: next_task_id,
        }, { timeout: 25000 });
        const newRunId = newRun.data?.id;
        if (embedded && onRunFinished) {
          await onRunFinished({ appraisal_id, next_run_id: newRunId, next_status_key: null });
          setFinalizing(false);
          return;
        }
        if (newRunId) {
          navigate(`/workflow/${newRunId}`);
        } else {
          navigate(`/workflow/appraisal/${appraisal_id}`);
        }
        return;
      }

      // Case B · Last task in the status — hop to next status.
      //   S8a bug 3 fix (2026-02c) · when the backend auto-created the
      //   next-status run, jump straight into it instead of the
      //   appraisal-scoped picker page.
      if (next_run_id) {
        if (embedded && onRunFinished) {
          await onRunFinished({ appraisal_id, next_run_id, next_status_key });
          setFinalizing(false);
          return;
        }
        return navigate(`/workflow/${next_run_id}`);
      }
      if (embedded && onRunFinished) {
        await onRunFinished({ appraisal_id, next_status_key });
        setFinalizing(false);
        return;
      }
      if (appraisal_id) {
        const q = next_status_key ? `?status=${next_status_key}` : '';
        navigate(`/workflow/appraisal/${appraisal_id}${q}`);
      } else {
        navigate('/workflow/start');
      }
    } catch (e) {
      // v10.14 · surface a friendly timeout message so the operator knows
      // to check network and retry, instead of a raw axios error string.
      if (e?.response?.status === 409 && e.response.data?.code === 'duplicate_appraisal') {
        setDupe(e.response.data);
        setFinalizing(false);
        return;
      }
      let msg = e?.response?.data?.detail || e.message || 'Continue failed';
      if (e?.code === 'ECONNABORTED' || /timeout/i.test(msg)) {
        msg = 'Continue timed out (>25s). Your network stalled — click Continue again to retry, or refresh the page. The task IS already saved.';
      }
      setFinalizeError(msg);
      setFinalizing(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-400"><Loader2 className="animate-spin" size={32} /></div>;
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="max-w-md text-center">
          <div className="text-rose-700 bg-rose-50 border border-rose-200 rounded p-4 mb-4">{error}</div>
          <button onClick={() => navigate('/workflow/start')} className="text-sky-600 hover:underline">← Back to Start</button>
        </div>
      </div>
    );
  }
  if (!run) return null;

  const task = run._task || {};
  const totalActions = flat.length;
  const doneCount = (run.completed_actions || []).length;
  const progressPct = totalActions ? Math.round((doneCount / totalActions) * 100) : 0;

  const Renderer = current ? dispatchRenderer(current.action.type) : null;

  // Workflow context banner — shows the operator which client + property this
  // run belongs to as soon as they're captured. Reads from captured_data.
  const cd = run.captured_data || {};
  const clientForm = cd.client_intake_form || {};
  const clientRec = cd.client_record || {};
  const propRec = cd.property_record || {};
  const appraisalRec = cd.appraisal || cd.appraisal_record || {};
  const clientName = [clientRec.name_first || clientForm.name_first, clientRec.name_last || clientForm.name_last].filter(Boolean).join(' ').trim();
  const clientNo = clientRec.client_no;
  const clientId = cd.client_id;
  const propertyId = cd.property_id;
  const propertyNo = propRec.property_no;
  const propertyAddr = [propRec.property_address_num, propRec.property_address_street].filter(Boolean).join(' ');
  // iter303ak-fix2 · Appraisal A# chip so the runner banner mirrors the
  // Console header format (A · C · P · client · address).
  const appraisalId = cd.appraisal_id;
  const appraisalNo = appraisalRec.appraisal_no;
  const hasContext = !!(clientId || clientName || appraisalId);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 py-3 flex items-center gap-3">
          {!embedded && (
            <>
              <button onClick={() => navigate('/workflow/start')}
                data-testid="wf-home-btn"
                className="text-slate-500 hover:text-slate-700 flex items-center gap-1 text-sm">
                <Home size={14} /> Runner Home
              </button>
              <span className="text-slate-300">·</span>
            </>
          )}
          <div className="text-sm">
            <span className="text-slate-500">Status:</span> <b className="text-slate-800">{stageLabels[run.status_key] || run.status_key.replace(/_/g, ' ')}</b>
            <span className="mx-2 text-slate-300">·</span>
            <span className="text-slate-500">Task:</span> <b className="text-slate-800" data-testid="wf-task-label">{task.label}</b>
            <span className="mx-2 text-slate-300">·</span>
            <span className="text-slate-500">Entry:</span> <b className="text-slate-800">{run.entry_choice === 'new_client' ? 'New Client' : 'Existing Client'}</b>
          </div>
          <span className="flex-1" />
          <button onClick={abandon} data-testid="wf-abandon-btn"
            className="text-xs text-rose-400 hover:text-rose-600 flex items-center gap-1"><X size={12} /> Abandon</button>
        </div>
        {/* Progress bar */}
        <div className="max-w-3xl mx-auto px-6 pb-2">
          <div className="flex items-center gap-3 text-xs text-slate-500">
            <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full bg-sky-500 transition-all" style={{ width: `${progressPct}%` }} data-testid="wf-progress-bar" />
            </div>
            <span data-testid="wf-progress-label">{doneCount} / {totalActions} · {progressPct}%</span>
          </div>
        </div>
        {/* Workflow context — Client / Property chips */}
        {hasContext && (
          <div className="max-w-3xl mx-auto px-6 pb-3">
            <div className="flex items-center gap-2 flex-wrap text-xs" data-testid="wf-context-banner">
              <span className="uppercase tracking-widest text-slate-400 font-semibold">Working on</span>
              {/* iter303ak-fix2 · Appraisal A# pill (was missing — the
                  runner showed no appraisal identity even though the
                  Console header did). */}
              {(appraisalId || appraisalNo != null) && (
                <a href={appraisalId ? `/appraisals/${appraisalId}` : '#'} target="_blank" rel="noopener noreferrer"
                  data-testid="wf-context-appraisal"
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 border border-amber-200 rounded text-amber-800 hover:bg-amber-100">
                  {appraisalNo != null
                    ? <><b>A{appraisalNo}</b></>
                    : <>Appraisal <code className="text-[11px]">{String(appraisalId).slice(0, 8)}…</code></>
                  }
                </a>
              )}
              {clientName ? (
                <a href={clientId ? `/clients/${clientId}` : '#'} target="_blank" rel="noopener noreferrer"
                  data-testid="wf-context-client"
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-sky-50 border border-sky-200 rounded text-sky-800 hover:bg-sky-100">
                  <b>{clientName}</b>
                  {clientNo != null && <span className="text-sky-500">· C{clientNo}</span>}
                </a>
              ) : clientId && (
                <span data-testid="wf-context-client-id"
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-sky-50 border border-sky-200 rounded text-sky-800">
                  Client id <code className="text-[11px]">{String(clientId).slice(0, 8)}…</code>
                </span>
              )}
              {propertyId && (
                <a href={`/properties/${propertyId}`} target="_blank" rel="noopener noreferrer"
                  data-testid="wf-context-property"
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 border border-emerald-200 rounded text-emerald-800 hover:bg-emerald-100">
                  {propertyNo != null ? <><b>P{propertyNo}</b>{propertyAddr && <span className="text-emerald-500 ml-1">· {propertyAddr}</span>}</> : <>Property <code className="text-[11px]">{String(propertyId).slice(0, 8)}…</code></>}
                </a>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="max-w-3xl mx-auto px-6 py-8">
        {isComplete ? (() => {
          // iter303ai-fix10 · Task-sibling aware CTA labelling. Look at
          // this task's position in the status; if more sibling tasks
          // remain to the RIGHT, the primary CTA reads "Continue to Task
          // N of M — {label}" (finalize will start a new run for it). If
          // this IS the last task in the status, the CTA reads "Continue
          // to {next status}" (finalize will unlock the next status).
          // iter303aj-fix7 · Exclusive-mode statuses (Client Data · New
          // XOR Existing) skip the sibling-Next prediction: completing
          // ANY sibling completes the whole status, so the CTA always
          // reads "Continue to {next status}".
          const taskId = run.task_id;
          const myIdx = siblingTasks.findIndex(t => t.id === taskId);
          const nextSibling = myIdx >= 0 ? siblingTasks[myIdx + 1] : null;
          const isExclusive = siblingMode === 'exclusive';

          let ctaLabel = null;
          if (nextSibling && !isExclusive) {
            const total = siblingTasks.length;
            const nextNo = myIdx + 2; // 1-indexed for humans
            ctaLabel = `Continue to Task ${nextNo} of ${total} — ${nextSibling.label}`;
          } else {
            const currentStatusIdx = Object.keys(STATUS_LABELS).indexOf(run.status_key);
            const nextStatusKey = Object.keys(STATUS_LABELS)[currentStatusIdx + 1];
            const nextStatusLabel = nextStatusKey ? stageLabels[nextStatusKey] : null;
            if (nextStatusLabel) ctaLabel = `Continue to ${nextStatusLabel}`;
          }
          return (
          <div className="text-center py-12 bg-white border border-emerald-200 rounded-lg" data-testid="wf-task-complete">
            <CheckCircle2 size={48} className="mx-auto text-emerald-500 mb-3" />
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Task complete!</h2>
            <p className="text-slate-500 mb-6">All {totalActions} steps of &ldquo;{task.label}&rdquo; are done.</p>
            <div className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded p-4 max-w-lg mx-auto text-left">
              <div className="font-semibold text-slate-700 mb-2">Captured data:</div>
              {Object.keys(run.captured_data || {}).length === 0 ? (
                <div className="text-slate-400 italic">No data captured</div>
              ) : (
                <dl className="text-xs space-y-1">
                  {Object.entries(run.captured_data).map(([k, v]) => (
                    <div key={k} className="flex gap-2">
                      <dt className="text-slate-500 font-mono">{k}:</dt>
                      <dd className="text-slate-700 min-w-0 break-all line-clamp-2">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>
            {finalizeError && (
              <div className="mt-4 max-w-lg mx-auto text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3 text-left"
                data-testid="wf-finalize-error">
                {finalizeError}
              </div>
            )}
            {dupe && (
              <DuplicateAppraisalPrompt dupe={dupe} busy={finalizing}
                onUseExisting={() => finalize({ use_existing_appraisal_id: dupe.existing?._id })}
                onCreateAnyway={() => finalize({ confirm_duplicate: true })} />
            )}
            <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
              {ctaLabel && (
                <button
                  onClick={() => finalize()}
                  disabled={finalizing || !!dupe}
                  data-testid="wf-continue-next-status"
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-emerald-600 text-white font-semibold rounded hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm">
                  {finalizing ? (
                    <><Loader2 size={16} className="animate-spin" /> Finalizing…</>
                  ) : (
                    <>{ctaLabel} <ArrowRight size={16} /></>
                  )}
                </button>
              )}
              <button onClick={() => embedded && onRunFinished ? onRunFinished() : navigate('/workflow/start')}
                data-testid="wf-done-return"
                disabled={finalizing}
                className="px-6 py-2.5 bg-white border-2 border-slate-300 text-slate-700 font-semibold rounded hover:border-sky-400 hover:text-sky-700 disabled:opacity-50">
                {embedded ? 'Back to Console' : 'Return to Runner Home'}
              </button>
            </div>
          </div>
          );
        })() : current ? (
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
            <div className="mb-4 flex items-center gap-2">
              <span className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Action {currentIdx + 1} of {totalActions}</span>
              {current.depth > 0 && (
                <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded ${current.depth === 1 ? 'bg-purple-100 text-purple-700' : 'bg-orange-100 text-orange-700'}`}>
                  {current.depth === 1 ? 'sub-action' : 'sub-sub-action'}
                </span>
              )}
              {saving && <span className="text-xs text-slate-400 flex items-center gap-1"><Loader2 size={10} className="animate-spin" /> Saving…</span>}
            </div>
            {/* iter303ak-fix7 · Sticky "Return to Preview" pill — visible
                only when the operator was diverted here from another
                action (e.g. Completeness Preview → Jump to Action N). One
                click hops them back to where they were. */}
            {divertedFromActionId && divertedFromActionId !== current.action.id && (
              <div className="mb-3 rounded-lg border border-teal-300 bg-teal-50 px-3 py-2 flex items-center justify-between" data-testid="wf-diversion-banner">
                <div className="text-xs text-teal-800">
                  <b>Diverted from Completeness Preview</b> — finish the fix here, then jump back to review.
                </div>
                <button
                  type="button"
                  onClick={() => jumpToAction(divertedFromActionId, { divertedFrom: null })}
                  className="px-3 py-1 text-xs font-semibold bg-teal-600 text-white rounded hover:bg-teal-700"
                  data-testid="wf-return-to-preview-btn"
                >
                  ↩ Return to Preview
                </button>
              </div>
            )}
            {Renderer && (
              <Renderer
                action={current.action}
                primitive={current.primitive}
                capturedData={run.captured_data || {}}
                value={inflightValue}
                onChange={setInflightValue}
                onNext={advance}
                onBack={back}
                onCancel={cancel}
                canBack={currentIdx > 0}
                statusKey={run.status_key}
                taskId={run.task_id}
                runId={runId}
                api={api}
                onCapturedDataRefresh={() => loadRun({ silent: true })}
                onTemplateChanged={() => loadRun({ silent: true })}
                onJumpTo={jumpToAction}
                divertedFromActionId={divertedFromActionId}
                actionFlat={flat}
              />
            )}
          </div>
        ) : (
          <div className="text-center py-12" data-testid="wf-no-current-action">
            <div className="text-slate-400 mb-4">No current action.</div>
            <button
              onClick={cancel}
              data-testid="wf-no-current-return"
              className="px-4 py-2 bg-sky-600 text-white text-sm font-semibold rounded hover:bg-sky-700"
            >
              Return to task selection →
            </button>
            <div className="text-[11px] text-slate-400 mt-2">
              This run has no pointer to a live action — probably because the pointer was moved to an action that isn&apos;t in the current task. Return to task selection to pick a fresh path.
            </div>
          </div>
        )}

        {/* Debug: captured data pane (dev aid) */}
        <details className="mt-6 text-xs">
          <summary className="text-slate-400 cursor-pointer">Captured data so far ({Object.keys(run.captured_data || {}).length})</summary>
          <pre className="mt-2 p-3 bg-slate-100 border border-slate-200 rounded overflow-auto text-slate-700">
{JSON.stringify(run.captured_data || {}, null, 2)}
          </pre>
        </details>
      </div>
    </div>
  );
}
